<script setup lang="ts">
import { onBeforeUnmount, onMounted, watch } from "vue";
import { useRoute } from "vue-router";
import AuthDebugger from "~/components/AuthDebugger.vue";
import { useOrgCSS } from "~/composables/useOrgCSS";
import { useSiteWideHubManager } from "~/composables/useSiteWideHubManager";
import { authStore } from "~/stores/authStore";
import { usePdfLoadingStore } from "~/stores/pdfLoadingStore";
import { setupTokenRequestHandler } from "~/utils/iframeAuth";
import { getEnvConfig } from "./services/appConfig";
import { getModeDisplay } from "./utils/miscUtils";

const isDevelopment = import.meta.env.DEV;
const useSignalR = true; // keep this true to enable SignalR hubs

const route = useRoute();
const serverStatus = useSiteWideHubManager();

// Initialize org-specific CSS handling (automatically applies on org info load)
useOrgCSS();

// Set up iframe token request handler
let cleanupTokenHandler: (() => void) | null = null;

onMounted(() => {
  // Set up message handler for iframe token requests
  cleanupTokenHandler = setupTokenRequestHandler(async () => {
    return await authStore.getAccessTokenAsync();
  });

  if (getEnvConfig().env === "live") {
    // we are not displaying the mode display, but we still want to log it for reference if needed
    console.log(getModeDisplay());
  }
});

// Watch for authentication state changes and setup hub connection when authenticated
watch(
  () => authStore.isAuthenticated,
  (isAuthenticated) => {
    if (isAuthenticated && useSignalR) {
      // Add a small delay to avoid conflicts with other token requests during app initialization
      setTimeout(() => {
        serverStatus.setupHubConnection();
      }, 1000);
    }
  },
);
onBeforeUnmount(() => {
  // Clean up the PDF progress listener if it was ever attached by makePdf().
  usePdfLoadingStore().cleanupPdfProgressListener?.();
  serverStatus.unmountHandler();

  // Clean up iframe token request handler
  if (cleanupTokenHandler) {
    cleanupTokenHandler();
  }
});
</script>

<template>
  <RouterView :key="route.fullPath" />
  <!-- Only show in development -->
  <AuthDebugger v-if="isDevelopment" />
</template>

<style lang="less">
body {
  // use body to make these overrides more specific than the default styles
  // TODO: better would be to ensure that ElementPlus styles are loaded before this file
  --el-font-size-base: 1.2rem;
  --el-checkbox-font-size: 1.2rem; // used?
  font-size: var(--el-font-size-base);

  .el-collapse {
    --el-collapse-header-height: 24px;
  }
  .el-checkbox__input.is-disabled .el-checkbox__inner {
    border-color: var(--el-color-primary-light-8);
  }
  .el-checkbox__label {
    font-size: inherit;
  }

  .el-auto-resizer {
    // shrink to not be too tall
    // TODO: need to determine how best to handle this on various pages
    // using height: 100% on parent containers does not solve the issue
    max-height: 80vh !important;
  }

  .SectionComponent,
  .workflow-drawer .el-collapse-item__header {
    border-radius: var(--el-border-radius-base);
  }

  /* to center the check mark inside te checkbox */
  // --> do this where needed
  // .el-checkbox__inner::after {
  //   left: 5px;
  //   top: 2px;
  // }

  .el-container {
    background-color: var(--background-color);
  }

  .el-switch {
    width: fit-content;
  }
  .el-select-group__title {
    padding-left: 10px;
  }
  .el-collapse-item__content {
    font-size: inherit;
  }
  .el-transfer {
    --el-transfer-panel-width: fit-content;
    .el-transfer-panel
      .el-transfer-panel__header
      .el-checkbox
      .el-checkbox__label
      span {
      position: relative;
      right: unset;
      top: unset;
      transform: unset;
    }
    .el-checkbox__label {
      width: 100%;
      display: flex;
      gap: 0.5em;
      justify-content: space-between;
      align-items: center;
      padding-right: 0.5em;
    }
  }

  .el-tabs {
    // we are using type="card"
    &.el-tabs--card {
      > .el-tabs__header {
        border-bottom-color: var(--el-color-primary-light-3);

        .el-tabs__item {
          transition: all 0.1s ease-in-out;
          &.is-active {
            margin-bottom: 1px;
            border-bottom-color: var(--el-color-primary);
            background-color: var(--el-color-primary-light-8);
            color: var(--el-color-primary);
            font-weight: 600;
          }
        }
      }
    }
  }

  .el-table {
    .cell {
      overflow-wrap: normal;
    }
  }
  .el-tag {
    min-height: 24px;
    height: unset;
  }

  .el-message-box__container {
    min-height: 4em;
    .el-icon {
      font-size: 1.3em;
    }
    p {
      margin: 0;
    }
  }
}
body:not(.forPdf) {
  .inPdf {
    display: none;
  }
}

h1 {
  font-size: 1.9rem;
  font-weight: 500;
  color: var(--el-color-primary);
}
h2 {
  font-size: 1.75rem; // match host site size
  font-weight: 500;
  color: var(--el-color-primary);
}

h3 {
  font-size: 1.75rem;
  font-weight: 500;
  color: var(--el-color-primary);
}

.mainContainer {
  height: 100vh;
}

.pageTipIcon {
  position: fixed;
  bottom: calc(1em + 4px);
  right: 19px;
  z-index: 1000;
  opacity: 0.8;

  &:hover {
    opacity: 1;
  }
}

.tipsDialog {
  .key {
    padding: 3px 6px 3px;
    background-color: #1f1f1f;
    color: #faf67a;
    border-radius: 5px;
    display: inline-block;
    text-align: center;
    min-width: 9px;
    font-family: monospace;
  }

  .key + .key {
    margin-left: 1px;
  }

  table {
    margin-top: -30px;
  }

  td,
  th {
    padding: 3px 5px;
    vertical-align: middle;
  }

  td.left {
    text-align: right;
    white-space: nowrap;
  }

  thead {
    th {
      text-align: left;
      padding: 1em 0 0 0;
      border-bottom: 1px solid black;
    }
  }
}

.clickable {
  cursor: pointer;
}

.devBranchName {
  position: fixed;
  bottom: 0;
  left: 0;
  background-color: #f0f0f0;
  padding: 1px 0.5em;
  font-size: 0.75em;
  color: #666;
  cursor: pointer;
}

i.el-table-v2__sort-icon {
  visibility: hidden;
  display: block;
  &.is-sorting {
    visibility: visible;
  }
}
.is-sortable:hover i.el-table-v2__sort-icon {
  visibility: visible;
}

.el-link [class*="el-icon"] + span {
  margin-left: 6px;
}

.flexRow {
  display: flex;
  flex-direction: row;
  margin: 0.5em 1em 1em 1em;
  justify-content: start;
  align-items: flex-end; // some elements are not the same height
  gap: 1em;
  height: fit-content;

  .el-input {
    width: 200px;
  }
}

.el-input__wrapper {
  padding: 1px;
  .el-input__inner {
    padding: 0 10px;
  }
  .el-input__prefix {
    padding-left: 6px;
  }
  .el-input__suffix {
    padding-right: 6px;
  }
}

.el-input.is-disabled {
  cursor: default;
  .el-input__wrapper {
    cursor: default;
    .el-input__inner {
      cursor: default;
      padding: 0 10px;
    }
  }
}

.el-input__inner {
  font-family: poppins, sans-serif;
}

.el-button.addButton {
  font-size: 15px;
  background-color: var(--el-color-primary);
  color: white;

  &:hover {
    background-color: var(--el-color-primary-light-3);
    color: white;
  }
}
.showingInTable {
  font-size: 14px;
  font-weight: 600;
}

.el-input-tag__input {
  font-family: poppins, sans-serif;
}
body .el-tour {
  --el-tour-width: 520px;
  --el-tour-padding-primary: 12px;
  --el-tour-font-line-height: var(--el-font-line-height-primary);
  --el-tour-title-font-size: 1.35rem;
  --el-tour-title-text-color: var(--subheading-color);
  --el-tour-title-font-weight: 400;
  --el-tour-close-color: var(--el-color-info);
  --el-tour-font-size: 1.2rem;
  --el-tour-color: var(--el-text-color-primary);
  --el-tour-bg-color: var(--el-bg-color);
  --el-tour-border-radius: 4px;

  --el-font-size-base: var(--el-tour-font-size);
  font-size: var(--el-tour-font-size);

  // match IntakeLayout.vue
  @info-color: #4b8483;

  .el-button--small {
    &.el-button--primary {
      background-color: @info-color;
      color: white;
      border-color: @info-color;
    }
    padding: 0.75em 0.5em;
    font-size: var(--el-tour-font-size);
  }
}
.tox.tox-silver-sink.tox-tinymce-aux {
  z-index: 3000;
}

@media print {
  // improve printing of workflows
  body {
    color: #fff;
  }
  body.compliance {
    --el-font-size-base: 10pt;
    .el-drawer,
    .el-overlay {
      position: static;
    }
  }
  .el-empty {
    display: none;
  }
  .el-main {
    padding: 0;
  }
  #app {
    position: relative;
  }
  .compliance.mainContainer {
    .homeMain {
      height: auto;
    }
  }
  li + li {
    margin-top: 1em;
  }
  li {
    padding-left: 0.5em;
  }
  .RichTextComponent {
    page-break-inside: avoid;
  }
  body .el-container {
    height: auto;
  }
  .el-drawer__body,
  .form-designer-page,
  .formContainer,
  .form-designer {
    height: auto;
  }
}
</style>
